import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const reviews = [
  {
    name: "Angela Phillips",
    avatar: "AP",
    rating: 5,
    text: "Revival Tech did an amazing job helping create a new logo for my expanding business. They showed such creativity and attention to detail and were truly a joy to work with. Thank you, Revival Tech!",
  },
  {
    name: "Rosemaryann Palma",
    avatar: "RP",
    rating: 5,
    text: "I can't say enough good things about Revival Tech! Their expertise in boosting our social media presence has been nothing short of amazing. From the initial consultation to the execution of their strategies, every step has been smooth and effective. Revival Tech truly understands the nuances of digital marketing and knows how to leverage each platform to maximize visibility and engagement. The team is incredibly responsive and supportive — working with them has been a game-changer for our business!",
  },
  {
    name: "June Shadduck",
    avatar: "JS",
    rating: 5,
    text: "It was a true pleasure working with Revival Tech. They created a promo for our Haunted House — a video, banner, and flyers. The video was simple, creative and scary. I had many changes as more sponsors were added, and the changes were done very quickly. I will definitely use them again!",
  },
  {
    name: "Tiana Matthews",
    avatar: "TM",
    rating: 5,
    text: "Revival Tech Firm rocks! They nailed our website and digital marketing needs with style. Super professional team that's easy to work with. Highly recommend them for anyone wanting top-notch online solutions!",
  },
  {
    name: "Lee Gibson",
    avatar: "LG",
    rating: 5,
    text: "I was in desperate need of enhancing my LinkedIn profile after I was told about my position going away. I worked closely with Gary to get my profile enhanced, modified my resume and help with AI driven feeds aligned to my area of experience. I am ready now to look for jobs knowing my profile accurately represents my capabilities as a senior product manager.",
  },
  {
    name: "David Smith",
    avatar: "DS",
    rating: 5,
    text: "My experience was excellent! Not only are the service and product top-tier, but I also gained a lot of knowledge and insight on how to grow my brand. I would highly recommend them!",
  },
  {
    name: "Christine Peth",
    avatar: "CP",
    rating: 5,
    text: "Emily is so easy to work with and is incredibly talented! I will be using their services again!",
  },
];

const StarRating = ({ rating }) => (
  <div className="flex gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <SafeIcon
        key={i}
        icon={FiIcons.FiStar}
        className="text-base text-yellow-400"
        style={{ fill: 'currentColor' }}
      />
    ))}
  </div>
);

const avatarColors = [
  'from-brand-accent to-brand-gradEnd',
  'from-purple-400 to-brand-gradEnd',
  'from-pink-400 to-brand-accent',
  'from-green-400 to-brand-accent',
  'from-orange-400 to-pink-500',
  'from-brand-gradEnd to-purple-500',
  'from-teal-400 to-brand-accent',
];

const Reviews = () => {
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setActive((prev) => (prev + 1) % reviews.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (index) => {
    setDirection(index > active ? 1 : -1);
    setActive(index);
  };

  const variants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 60 : -60 }),
    center: { opacity: 1, x: 0 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -60 : 60 }),
  };

  return (
    <section id="reviews" className="py-24 bg-brand-secondary relative overflow-hidden">
      <motion.div
        className="absolute top-0 left-0 w-full h-full pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 50%, rgba(59,223,248,0.06) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(59,0,255,0.08) 0%, transparent 50%)',
        }}
      />
      <motion.div
        className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full border border-white/5 pointer-events-none"
        animate={{ rotate: 360 }}
        transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
      />

      <div className="container mx-auto px-6 max-w-5xl relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <img
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png"
              alt="Google"
              className="h-5 object-contain brightness-200"
            />
            <span className="text-sm font-bold text-brand-accent uppercase tracking-widest">Reviews</span>
          </div>
          <h3 className="text-3xl md:text-5xl font-extrabold text-white">What Our Clients Say</h3>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <SafeIcon key={i} icon={FiIcons.FiStar} className="text-yellow-400 text-xl" style={{ fill: 'currentColor' }} />
              ))}
            </div>
            <span className="text-white font-bold text-lg">5.0</span>
            <span className="text-gray-400 text-sm">· {reviews.length} Google Reviews</span>
          </div>
          <motion.div
            className="h-1 bg-brand-accent mx-auto mt-6 rounded-full"
            initial={{ width: 0 }}
            whileInView={{ width: 96 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
          />
        </motion.div>

        {/* Main Carousel */}
        <div className="relative min-h-[300px] flex items-center">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={active}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.45, ease: 'easeInOut' }}
              className="w-full"
            >
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 md:p-12 relative">
                <div className="absolute top-6 right-8 text-8xl text-brand-accent/10 font-serif leading-none select-none">"</div>

                <div className="flex items-start gap-5 mb-6">
                  <motion.div
                    className={`w-14 h-14 rounded-full bg-gradient-to-br ${avatarColors[active % avatarColors.length]} flex items-center justify-center text-white font-extrabold text-lg flex-shrink-0 shadow-lg`}
                    whileHover={{ scale: 1.1 }}
                  >
                    {reviews[active].avatar}
                  </motion.div>
                  <div>
                    <h4 className="text-white font-bold text-lg">{reviews[active].name}</h4>
                    <div className="flex items-center gap-3 mt-1">
                      <StarRating rating={reviews[active].rating} />
                    </div>
                  </div>
                </div>

                <p className="text-gray-300 text-base md:text-lg leading-relaxed italic">
                  "{reviews[active].text}"
                </p>

                <div className="flex items-center gap-2 mt-6 pt-6 border-t border-white/10">
                  <SafeIcon icon={FiIcons.FiCheckCircle} className="text-brand-accent text-sm" />
                  <span className="text-gray-500 text-xs">Verified Google Review</span>
                  <a
                    href="https://g.page/r/CbSN73Q7D-GEEBM/review"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ml-auto text-xs text-brand-accent hover:underline flex items-center gap-1"
                  >
                    Leave a Review <SafeIcon icon={FiIcons.FiExternalLink} className="text-xs" />
                  </a>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Arrows + Dots */}
        <div className="flex items-center justify-center gap-6 mt-8">
          <motion.button
            onClick={() => goTo((active - 1 + reviews.length) % reviews.length)}
            className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-brand-accent hover:text-brand-secondary transition-colors"
            whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          >
            <SafeIcon icon={FiIcons.FiChevronLeft} />
          </motion.button>

          <div className="flex gap-2 flex-wrap justify-center">
            {reviews.map((_, i) => (
              <motion.button
                key={i}
                onClick={() => goTo(i)}
                className={`rounded-full transition-all duration-300 ${i === active ? 'w-8 h-3 bg-brand-accent' : 'w-3 h-3 bg-white/30 hover:bg-white/60'}`}
                whileHover={{ scale: 1.2 }}
                whileTap={{ scale: 0.9 }}
              />
            ))}
          </div>

          <motion.button
            onClick={() => goTo((active + 1) % reviews.length)}
            className="w-10 h-10 rounded-full bg-white/10 border border-white/20 flex items-center justify-center text-white hover:bg-brand-accent hover:text-brand-secondary transition-colors"
            whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
          >
            <SafeIcon icon={FiIcons.FiChevronRight} />
          </motion.button>
        </div>

        {/* Mini Cards Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-12">
          {reviews.map((r, i) => (
            <motion.button
              key={i}
              onClick={() => goTo(i)}
              className={`text-left p-3 rounded-xl border transition-all duration-300 cursor-pointer ${
                i === active
                  ? 'border-brand-accent bg-brand-accent/10'
                  : 'border-white/10 bg-white/5 hover:border-white/30'
              }`}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.97 }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${avatarColors[i % avatarColors.length]} flex items-center justify-center text-white font-bold text-xs flex-shrink-0`}>
                  {r.avatar}
                </div>
                <p className="text-white text-xs font-bold truncate">{r.name}</p>
              </div>
              <div className="flex gap-0.5 mb-1">
                {Array.from({ length: 5 }).map((_, j) => (
                  <SafeIcon key={j} icon={FiIcons.FiStar} className="text-yellow-400 text-xs" style={{ fill: 'currentColor' }} />
                ))}
              </div>
              <p className="text-gray-400 text-xs line-clamp-2">{r.text}</p>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Reviews;